export default function PalmBeachDestinationsSection() {
  const stats = [
    {
      value: "68%",
      label: "of Palm Beach requests are renewals discovered within 3 weeks of European summer departure",
    },
    {
      value: "4–8 weeks",
      label: "average notice for charity gala-related international trips",
    },
    {
      value: "Dec–Mar",
      label: "peak social season — passport renewals spike just before European summer bookings",
    },
    {
      value: "36-hour",
      label: "shortest yacht departure window we've accommodated from Palm Beach",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom",
      context: "London is the most frequent European destination for Palm Beach seasonal residents — whether for the Chelsea Flower Show, Wimbledon, or simply returning to a primary residence. The Cotswolds and Scotland also draw regular visitors. Many Palm Beach families maintain social and business ties in London that require annual or bi-annual travel.",
      urgency: "Most common renewal trigger: six-month validity rule for summer returns",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France",
      context: "Paris for fashion, art, and dining; the South of France for yacht season in Antibes, Cannes, and St. Tropez; Provence for extended summer stays. Palm Beach's French connection runs deep — many seasonal residents have properties or family ties in France.",
      urgency: "Charity gala connections often lead to last-minute Paris or Riviera invitations",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Italy",
      context: "Tuscany villa rentals, Rome cultural tours, Venice for the Biennale, and the Amalfi Coast for summer escapes. Italy is a staple of the Palm Beach summer calendar. The six-month validity requirement is particularly strict for Schengen entry through Italian ports and airports.",
      urgency: "Villa rental deposits are non-refundable — passport delays can cost thousands",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The Bahamas is the most common short-notice yacht destination for Palm Beach boaters — a same-day or next-day departure is possible when weather conditions align. St. Barts, Antigua, and the BVIs follow for longer itineraries. All require valid passports, and the Bahamas requires six-month validity for entry.",
      urgency: "Yacht weather windows create the shortest-notice passport needs in Palm Beach",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Switzerland",
      context: "Gstaad, St. Moritz, and Zurich draw Palm Beach families for winter ski seasons and summer alpine retreats. Swiss precision extends to border control — expired or near-expired passports are not accommodated. Many Wellington equestrian families also travel to Geneva and Lausanne for show jumping and dressage events.",
      urgency: "Equestrian competition entry deadlines require confirmed travel months in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Spruce Meadows in Calgary is a major destination for Wellington equestrian competitors. Toronto and Montreal draw Palm Beach families for summer cultural trips and family visits. Canada requires a valid passport for all air arrivals, and many equestrian families travel with multiple minors whose five-year passports expire faster than expected.",
      urgency: "Spruce Meadows show entries close 6–8 weeks before competition dates",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Palm Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Palm Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Palm Beach travelers do not follow typical vacation patterns. Their destinations reflect dual-residency lifestyles, yacht itineraries, equestrian competition schedules, and European summer traditions. These are the destinations that create the most passport urgency for our Palm Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Delray Beach", href: "/cities/delray-beach/" },
              { name: "Naples", href: "/cities/naples/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Miami", href: "/cities/miami/" },
              { name: "Sarasota", href: "/cities/sarasota/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}